/**
 * Conflict Checker Service
 * 
 * Prevents double-bookings by checking both Supabase (source of truth) and local IndexedDB.
 * This ensures that even in offline scenarios, we can detect potential conflicts.
 * 
 * For public-facing reservation systems, this is critical to prevent:
 * 1. Two users booking the same table at the same time
 * 2. Overbooking when multiple devices are used
 * 3. Race conditions during high-traffic periods
 */

import { reservationService } from './supabaseService';
import localDatabase from './localDatabase';
import { getMaxTablesOccupied } from './restaurantSettings';

// Duration of a reservation in minutes (2 hours + 5 min cleanup buffer)
const RESERVATION_DURATION_MINUTES = 125;

interface ConflictCheckParams {
  tenantId: string;
  date: string;           // YYYY-MM-DD format
  time: string;           // HH:MM format
  shiftId: string;
  tableId?: string;       // Optional - if not provided, checks all tables
  excludeId?: string;     // Exclude this reservation ID (for edits)
  partySize?: number;     // For capacity checks
}

interface ConflictResult {
  hasConflict: boolean;
  conflictType?: 'time_overlap' | 'table_occupied' | 'capacity_exceeded' | 'supabase_error';
  message?: string;
  conflictingReservations?: Array<{
    id: string;
    table_id: string;
    time: string;
    customer_name?: string;
  }>;
}

function getReservationTableIds(
  reservation: { table_id?: string | null; table_ids?: string[] | null },
): string[] {
  if (Array.isArray(reservation.table_ids) && reservation.table_ids.length > 0) {
    return reservation.table_ids;
  }
  return reservation.table_id ? [reservation.table_id] : [];
}

/**
 * Check if two time slots overlap
 * Each reservation is assumed to last RESERVATION_DURATION_MINUTES
 */
export function doTimesOverlap(time1: string, time2: string): boolean {
  const [h1, m1] = time1.split(':').map(Number);
  const [h2, m2] = time2.split(':').map(Number);
  
  const start1 = h1 * 60 + m1;
  const end1 = start1 + RESERVATION_DURATION_MINUTES;
  
  const start2 = h2 * 60 + m2;
  const end2 = start2 + RESERVATION_DURATION_MINUTES;
  
  // Overlap if one starts before the other ends
  return start1 < end2 && start2 < end1;
}

/**
 * Check if a table is available for booking (status-wise)
 * Tables with status 'active' are available for booking
 * Tables with status 'inactive' or 'maintenance' are not available
 */
function isTableAvailableForBooking(table: { status?: string; is_active?: boolean }): boolean {
  // Check is_active boolean if present (from Supabase)
  if (typeof table.is_active === 'boolean') {
    return table.is_active;
  }
  // Check status field - 'active' means available for booking
  // 'inactive' or 'maintenance' means not available
  const status = table.status?.toLowerCase();
  return status === 'active' || status === 'available';
}

/**
 * Count how many tables are occupied at a given time slot
 * A table is "occupied" if it has a reservation that overlaps with the given time
 */
function countOccupiedTables(
  reservations: Array<{ id: string; table_id?: string; table_ids?: string[]; time: string; status: string }>,
  time: string,
  excludeId?: string
): number {
  // Get unique table IDs that have overlapping reservations
  const occupiedTableIds = new Set<string>();
  
  for (const r of reservations) {
    // Skip if this is the reservation being edited
    if (excludeId && r.id === excludeId) continue;
    
    // Skip if reservation doesn't have a table assigned
    const tableIds = getReservationTableIds(r);
    if (tableIds.length === 0) continue;
    
    // Skip non-blocking statuses
    if (!['confirmed', 'in-progress'].includes(r.status)) continue;
    
    // Check if times overlap
    if (doTimesOverlap(r.time, time)) {
      tableIds.forEach((tableId) => occupiedTableIds.add(tableId));
    }
  }
  
  return occupiedTableIds.size;
}

/**
 * Check for conflicts in local IndexedDB
 * This is used as a fallback when offline
 */
async function checkLocalConflicts(params: ConflictCheckParams): Promise<ConflictResult> {
  try {
    const allReservations = await localDatabase.getAllReservations();
    
    // Filter to same date and active statuses
    const sameDay = allReservations.filter(r => 
      r.date === params.date &&
      ['confirmed', 'in-progress'].includes(r.status) &&
      r.id !== params.excludeId
    );
    
    // Check capacity limit (max tables occupied at once)
    const maxTablesOccupied = getMaxTablesOccupied();
    if (maxTablesOccupied && maxTablesOccupied > 0) {
      const currentlyOccupied = countOccupiedTables(
        sameDay, 
        params.time, 
        params.excludeId
      );
      
      if (currentlyOccupied >= maxTablesOccupied) {
        return {
          hasConflict: true,
          conflictType: 'capacity_exceeded',
          message: `Έχει συμπληρωθεί η χωρητικότητα (${maxTablesOccupied} τραπέζια) για αυτή την ώρα`
        };
      }
    }
    
    // If checking a specific table, find conflicts
    if (params.tableId) {
      const tableConflicts = sameDay.filter(r => 
        getReservationTableIds(r).includes(params.tableId!) && 
        doTimesOverlap(r.time, params.time)
      );
      
      if (tableConflicts.length > 0) {
        // Get customer names for better error messages
        const customers = await localDatabase.getAllCustomers();
        const customerMap = new Map(customers.map(c => [c.id, c.name]));
        
        return {
          hasConflict: true,
          conflictType: 'table_occupied',
          message: `Το τραπέζι είναι ήδη κρατημένο στις ${tableConflicts[0].time}`,
          conflictingReservations: tableConflicts.map(r => ({
            id: r.id,
            table_id: r.table_id || '',
            time: r.time,
            customer_name: customerMap.get(r.customer_id) || 'Άγνωστος'
          }))
        };
      }
    } else {
      // Check if any tables are available at this time
      const allTables = await localDatabase.getAllTables();
      const availableTables = allTables.filter(table => {
        // Check if table is active and available for booking
        if (!isTableAvailableForBooking(table)) return false;
        
        // Check if this table has a conflicting reservation at this time
        const hasConflict = sameDay.some(r => 
          getReservationTableIds(r).includes(table.id) && 
          doTimesOverlap(r.time, params.time)
        );
        
        return !hasConflict;
      });
      
      if (availableTables.length === 0 && allTables.length > 0) {
        return {
          hasConflict: true,
          conflictType: 'time_overlap',
          message: 'Δεν υπάρχουν διαθέσιμα τραπέζια για αυτή την ώρα'
        };
      }
    }
    
    return { hasConflict: false };
  } catch (error) {
    console.error('[conflictChecker] Error checking local conflicts:', error);
    return { hasConflict: false }; // Don't block on local check errors
  }
}

/**
 * Check for conflicts in Supabase (source of truth)
 * This should be called when online before creating/updating reservations
 */
async function checkSupabaseConflicts(params: ConflictCheckParams): Promise<ConflictResult> {
  try {
    // If a specific table is requested, check if that table is available
    if (params.tableId) {
      const conflicts = await reservationService.checkConflicts(
        params.tenantId,
        params.date,
        params.time,
        params.shiftId,
        params.tableId,
        params.excludeId
      );
      
      if (conflicts.length > 0) {
        return {
          hasConflict: true,
          conflictType: 'table_occupied',
          message: `Το τραπέζι είναι ήδη κρατημένο για αυτή την ώρα`,
          conflictingReservations: conflicts.map(c => ({
            id: c.id,
            table_id: c.table_id || '',
            time: params.time
          }))
        };
      }
    }
    // If no specific table is requested, the local check already verified
    // that there are available tables. The actual table assignment will
    // happen during reservation creation in Supabase, which uses 
    // available_tables_for_shift with time-aware overlap checking.
    // We don't need to block here just because other reservations exist
    // at this time - that's expected and okay as long as tables are available.
    
    return { hasConflict: false };
  } catch (error) {
    console.error('[conflictChecker] Error checking Supabase conflicts:', error);
    return {
      hasConflict: false, // Don't block if Supabase is unreachable
      conflictType: 'supabase_error',
      message: 'Δεν ήταν δυνατός ο έλεγχος διαθεσιμότητας. Η κράτηση θα ελεγχθεί κατά τον συγχρονισμό.'
    };
  }
}

/**
 * Main conflict checking function
 * Checks both local and Supabase (if online) for conflicts
 */
export async function checkReservationConflicts(params: ConflictCheckParams): Promise<ConflictResult> {
  console.log('[conflictChecker] Checking conflicts for:', params);
  
  // Always check local first (fast)
  const localResult = await checkLocalConflicts(params);
  if (localResult.hasConflict) {
    console.log('[conflictChecker] Local conflict found:', localResult);
    return localResult;
  }
  
  // If online, also check Supabase (source of truth)
  if (navigator.onLine) {
    const supabaseResult = await checkSupabaseConflicts(params);
    if (supabaseResult.hasConflict) {
      console.log('[conflictChecker] Supabase conflict found:', supabaseResult);
      return supabaseResult;
    }
    
    // Return warning if Supabase check failed
    if (supabaseResult.conflictType === 'supabase_error') {
      return supabaseResult;
    }
  } else {
    console.log('[conflictChecker] Offline - using local check only');
  }
  
  return { hasConflict: false };
}

/**
 * Check if a specific table is available at a given time
 */
export async function isTableAvailable(
  tenantId: string,
  tableId: string, 
  date: string, 
  time: string, 
  shiftId: string,
  excludeReservationId?: string
): Promise<{ available: boolean; reason?: string }> {
  const result = await checkReservationConflicts({
    tenantId,
    tableId,
    date,
    time,
    shiftId,
    excludeId: excludeReservationId
  });
  
  return {
    available: !result.hasConflict,
    reason: result.message
  };
}

/**
 * Get all available tables for a given time slot
 */
export async function getAvailableTables(
  date: string,
  time: string,
  _shiftId: string,
  partySize?: number
): Promise<Array<{ id: string; number: number; seats: number; zone: string }>> {
  try {
    const allTables = await localDatabase.getAllTables();
    const allReservations = await localDatabase.getAllReservations();
    
    // Filter reservations for the same date and active statuses
    const sameDay = allReservations.filter(r => 
      r.date === date &&
      ['confirmed', 'in-progress'].includes(r.status)
    );
    
    // Find tables that are available
    const available = allTables.filter(table => {
      // Check if table is active and available for booking
      if (!isTableAvailableForBooking(table)) return false;
      
      // Check capacity
      if (partySize && table.seats < partySize) return false;
      
      // Check for time conflicts
      const hasConflict = sameDay.some(r => 
        r.table_id === table.id && 
        doTimesOverlap(r.time, time)
      );
      
      return !hasConflict;
    });
    
    return available.map(t => ({
      id: t.id,
      number: t.number,
      seats: t.seats,
      zone: t.zone
    }));
  } catch (error) {
    console.error('[conflictChecker] Error getting available tables:', error);
    return [];
  }
}

export default {
  checkReservationConflicts,
  isTableAvailable,
  getAvailableTables
};

